import React, { useState, useEffect, useCallback } from 'react';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerOrganisation, saveBuyerOrganisation, fetchBuyerMembers } from '../../lib/buyerApi';

const BuyerOrganisationPage: React.FC = () => {
  const [organisation, setOrganisation] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: '', organisation_type: 'business', registration_number: '', tax_id: '',
    contact_email: '', contact_phone: '', contact_person: '', address: '', city: '', country: 'Rwanda',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, memRes] = await Promise.allSettled([fetchBuyerOrganisation(), fetchBuyerMembers()]);
      if (orgRes.status === 'fulfilled' && orgRes.value.organisation) {
        const org = orgRes.value.organisation;
        setOrganisation(org);
        setForm({ name: org.name || '', organisation_type: org.organisation_type || 'business',
          registration_number: org.registration_number || '', tax_id: org.tax_id || '',
          contact_email: org.contact_email || '', contact_phone: org.contact_phone || '',
          contact_person: org.contact_person || '', address: org.address || '',
          city: org.city || '', country: org.country || 'Rwanda' });
      }
      if (memRes.status === 'fulfilled') setMembers(memRes.value.members || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Organisation name is required'); return; }
    setSaving(true); setSaved(false);
    try { await saveBuyerOrganisation(form); setSaved(true); load(); setTimeout(() => setSaved(false), 3000); }
    catch (err: any) { alert(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <BuyerPortalLayout title="Buyer Organisation">
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Save button */}
          <div className="flex justify-end">
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition">
              {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
            </button>
          </div>

          {/* Organisation Form */}
          <div className="bg-white border border-gray-100 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Organisation Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Organisation Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="e.g., Green Farms Ltd" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.organisation_type} onChange={e => setForm({ ...form, organisation_type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm">
                  <option value="business">Business</option><option value="cooperative">Cooperative</option>
                  <option value="ngo">NGO</option><option value="individual">Individual</option>
                </select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Registration #</label>
                <input type="text" value={form.registration_number} onChange={e => setForm({ ...form, registration_number: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                <input type="text" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input type="text" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
            </div>
          </div>

          {/* Members */}
          <div className="bg-white border border-gray-100 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Team Members ({members.length})</h3>
            {members.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No team members yet</p> : (
              <div className="divide-y divide-gray-100">
                {members.map((m: any) => (
                  <div key={m.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-emerald-700">{(m.full_name || 'U')[0].toUpperCase()}</span>
                      </div>
                      <div><p className="text-sm font-medium text-gray-900">{m.full_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{m.email}</p></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${m.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{m.status || 'Active'}</span>
                      <span className="text-xs text-gray-400 capitalize">{m.role || 'Member'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerOrganisationPage;
